import { openDB } from 'idb';

const DB_NAME = 'pwa-afericoes-db';
const DB_VERSION = 2;

const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    // Store para dados de bootstrap
    if (!db.objectStoreNames.contains('bootstrapData')) {
      db.createObjectStore('bootstrapData', { keyPath: 'key' });
    }

    // Store para empresas com bicos
    if (!db.objectStoreNames.contains('empresas')) {
      const empresasStore = db.createObjectStore('empresas', {
        keyPath: 'id'
      });
      empresasStore.createIndex('codEmpresa', 'codEmpresa');
      empresasStore.createIndex('updatedAt', 'updatedAt');
    }

    // Store para aferições locais
    if (!db.objectStoreNames.contains('afericoes')) {
      const afericoesStore = db.createObjectStore('afericoes', {
        keyPath: 'idLocal'
      });
      afericoesStore.createIndex('empresaId', 'empresaId');
      afericoesStore.createIndex('status', 'status');
      afericoesStore.createIndex('updatedAt', 'updatedAt');
    }

    // Store para uploads pendentes
    if (!db.objectStoreNames.contains('pendingUploads')) {
      db.createObjectStore('pendingUploads', { keyPath: 'uploadId' });
    }
  }
});

// Helpers para bootstrapData
export const saveBootstrapData = async (data) => {
  const db = await dbPromise;
  await db.put('bootstrapData', { key: 'master', data, updatedAt: new Date() });
};

export const getBootstrapData = async () => {
  const db = await dbPromise;
  const result = await db.get('bootstrapData', 'master');
  return result?.data || null;
};

// Helpers para aferições
export const saveAfericaoLocal = async (afericao) => {
  const db = await dbPromise;
  await db.put('afericoes', {
    ...afericao,
    updatedAt: new Date()
  });
};

export const getAfericaoLocal = async (idLocal) => {
  const db = await dbPromise;
  return await db.get('afericoes', idLocal);
};

export const getAfericoesPendentes = async () => {
  const db = await dbPromise;
  const index = db.transaction('afericoes').store.index('status');
  return await index.getAll('pendente_sync');
};

export const getAfericaoPorEmpresaEUsuario = async (empresaId, usuarioId) => {
  const db = await dbPromise;
  const allAfericoes = await db.getAll('afericoes');
  return allAfericoes.find(
    (a) =>
      a.empresaId === empresaId &&
      a.usuarioId === usuarioId &&
      (a.status === 'aberta' || a.status === 'pendente_sync')
  );
};

export const markAfericaoSynced = async (idLocal, serverId) => {
  const db = await dbPromise;
  const afericao = await db.get('afericoes', idLocal);
  if (afericao) {
    afericao.status = 'sincronizada';
    afericao.idServidor = serverId;
    afericao.updatedAt = new Date();
    await db.put('afericoes', afericao);
  }
};

// Helpers para pendingUploads
export const savePendingUpload = async (upload) => {
  const db = await dbPromise;
  await db.put('pendingUploads', {
    ...upload,
    uploaded: false
  });
};

export const getPendingUploads = async () => {
  const db = await dbPromise;
  const all = await db.getAll('pendingUploads');
  return all.filter((u) => !u.uploaded);
};

export const markUploadComplete = async (uploadId) => {
  const db = await dbPromise;
  const upload = await db.get('pendingUploads', uploadId);
  if (upload) {
    upload.uploaded = true;
    await db.put('pendingUploads', upload);
  }
};

export const deleteUpload = async (uploadId) => {
  const db = await dbPromise;
  await db.delete('pendingUploads', uploadId);
};

// Helpers para empresas
export const saveEmpresas = async (empresas) => {
  if (!empresas || !Array.isArray(empresas)) {
    console.error('[IndexedDB] Empresas inválidas:', empresas);
    throw new Error('Empresas deve ser um array');
  }

  const db = await dbPromise;
  const tx = db.transaction('empresas', 'readwrite');
  const store = tx.objectStore('empresas');
  
  // Limpar empresas antigas
  await store.clear();
  
  // Normalizar e salvar novas empresas
  let empresasSalvas = 0;
  let empresasIgnoradas = 0;
  
  for (const empresa of empresas) {
    if (!empresa || typeof empresa !== 'object') {
      console.warn('[IndexedDB] Empresa inválida, ignorando:', empresa);
      empresasIgnoradas++;
      continue;
    }

    // Normalizar o ID (pode vir como 'id' ou 'Id')
    const id = empresa.id || empresa.Id;
    if (!id) {
      console.error('[IndexedDB] Empresa sem ID, ignorando:', {
        codEmpresa: empresa.codEmpresa || empresa.CodEmpresa,
        nome: empresa.nome || empresa.Nome
      });
      empresasIgnoradas++;
      continue;
    }

    // Normalizar outros campos também
    const empresaNormalizada = {
      id: id, // Sempre usar 'id' minúsculo
      codEmpresa: empresa.codEmpresa || empresa.CodEmpresa || 0,
      nome: empresa.nome || empresa.Nome || '',
      latitude: empresa.latitude || empresa.Latitude || 0,
      longitude: empresa.longitude || empresa.Longitude || 0,
      timeZoneId: empresa.timeZoneId || empresa.TimeZoneId || '',
      bicos: Array.isArray(empresa.bicos) ? empresa.bicos : (Array.isArray(empresa.Bicos) ? empresa.Bicos : []),
      updatedAt: new Date()
    };

    try {
      await store.put(empresaNormalizada);
      empresasSalvas++;
    } catch (error) {
      console.error('[IndexedDB] Erro ao salvar empresa:', {
        id: empresaNormalizada.id,
        nome: empresaNormalizada.nome,
        error: error.message
      });
      empresasIgnoradas++;
    }
  }
  
  console.log('[IndexedDB] Empresas processadas:', {
    total: empresas.length,
    salvas: empresasSalvas,
    ignoradas: empresasIgnoradas
  });
  
  await tx.done;
  console.log('[IndexedDB] Empresas salvas:', empresas.length);
};

export const getAllEmpresas = async () => {
  const db = await dbPromise;
  const empresas = await db.getAll('empresas');
  console.log('[IndexedDB] Empresas recuperadas:', empresas.length);
  return empresas;
};

export const getEmpresaById = async (empresaId) => {
  const db = await dbPromise;
  return await db.get('empresas', empresaId);
};

export const clearEmpresas = async () => {
  const db = await dbPromise;
  const tx = db.transaction('empresas', 'readwrite');
  await tx.objectStore('empresas').clear();
  await tx.done;
  console.log('[IndexedDB] Cache de empresas limpo');
};

